import { z } from "zod";

/**
 * Shared DTO cho Admin Growth & Adoption funnel (US-AE10.F3-0034 · @UC9092 growth-adoption-funnel).
 * Một nguồn sự thật cho api (S01) và admin web (S03). Projection CHỈ-ĐỌC từ nguồn có sẵn
 * (User.lastActiveAt/emailVerifiedAt, GroupMember, MemberDue, Vote, Referral, AccountBalance) —
 * DTO chỉ mô tả read-only aggregate; KHÔNG field mutate.
 *
 * BẤT BIẾN (invariants):
 * - B1: AUM (tiền) = string minor-units (BigInt-safe, KHÔNG float/number JS). `aumMinor`.
 * - C2: aggregate no-PII — chỉ đếm/tổng, KHÔNG list cá nhân. AUM redact theo vai (null → "—").
 * - `.strict()` các object đọc — chống field lạ trên wire.
 * - Read-only: KHÔNG DTO ghi/mutate.
 */

// ============================================================
// @UC9092 — KPI growth (DAU/MAU/#groups/#users/AUM/churn + trend)
// ============================================================

/**
 * aumMinor = Σ AccountBalance.balanceMinor (kind=CASH) as string minor-units (no float).
 * `null` khi redact theo vai (SUPPORT/READ_ONLY) — UI hiển thị "—".
 */
const aumMinorString = z
  .string()
  .regex(/^-?\d+$/, "aumMinor phải là số nguyên minor units (không float)")
  .nullable();

/** Kỳ báo cáo (ISO from/to). */
export const growthPeriodSchema = z
  .object({
    from: z.string(),
    to: z.string(),
  })
  .strict();

export type GrowthPeriod = z.infer<typeof growthPeriodSchema>;

/** Một điểm xu hướng: metric + delta % so kỳ trước (number — tỉ lệ, KHÔNG phải tiền). */
export const growthTrendPointSchema = z
  .object({
    metric: z.enum(["dau", "mau", "groups", "users", "aum", "churn"]),
    deltaPct: z.number(),
  })
  .strict();

export type GrowthTrendPoint = z.infer<typeof growthTrendPointSchema>;

/**
 * KPI growth cho board (@UC9092 AC1). Counts = number nguyên (đếm — không phải tiền lớn). AUM =
 * string minor-units (B1, redact-able null). churn = rate [0..1]. trend = mảng delta% theo metric.
 */
export const growthKpiSchema = z
  .object({
    period: growthPeriodSchema,
    /** Distinct active users trong cửa sổ DAU (1 ngày). */
    dau: z.number().int().min(0),
    /** Distinct active users trong cửa sổ MAU (30 ngày). */
    mau: z.number().int().min(0),
    /** Số nhóm active tính đến `to`. */
    groups: z.number().int().min(0),
    /** Số user đã đăng ký tính đến `to`. */
    users: z.number().int().min(0),
    /** Σ AccountBalance CASH minor-units (string, no float) hoặc null (redact theo vai). */
    aumMinor: aumMinorString,
    /** Currency của AUM (đơn vị tiền chủ đạo, mặc định VND). */
    aumCurrency: z.string(),
    /** Tỉ lệ churn [0..1]: user từng active nhưng im lặng ≥ cửa sổ MAU. */
    churn: z.number().min(0).max(1),
    trend: z.array(growthTrendPointSchema),
  })
  .strict();

export type GrowthKpi = z.infer<typeof growthKpiSchema>;

// ============================================================
// @UC9092 — Funnel 6 bước + drop-off
// ============================================================

/** 6 bước phễu adoption (thứ tự cố định, monotonic ⊆). */
export const funnelStepKeySchema = z.enum([
  "registered",
  "verified",
  "joined",
  "paid",
  "voted",
  "invited",
]);

export type FunnelStepKey = z.infer<typeof funnelStepKeySchema>;

/** Một bước phễu: key + count + dropOffPct (so bước liền trước; bước đầu = 0). */
export const funnelStepSchema = z
  .object({
    step: funnelStepKeySchema,
    count: z.number().int().min(0),
    dropOffPct: z.number().min(0),
  })
  .strict();

export type FunnelStep = z.infer<typeof funnelStepSchema>;

export const funnelSchema = z
  .object({
    steps: z.array(funnelStepSchema),
  })
  .strict();

export type Funnel = z.infer<typeof funnelSchema>;

// ============================================================
// @UC9092 — Segment source (referral / organic) — NO social fabrication
// ============================================================

/**
 * Nguồn phân loại funnel. Enum giữ 3 giá trị cho tương thích, NHƯNG response CHỈ trả segment có dữ
 * liệu thật (referral + organic). "social" KHÔNG có nguồn phân loại acquisition ở funnel level
 * (SocialAccount = phương thức login, không phải channel) → OMIT (không bịa). Xem Decision Record §5.
 */
export const segmentSourceKeySchema = z.enum(["referral", "organic", "social"]);
export type SegmentSourceKey = z.infer<typeof segmentSourceKeySchema>;

export const segmentSourceSchema = z
  .object({
    source: segmentSourceKeySchema,
    funnel: funnelSchema,
  })
  .strict();

export type SegmentSource = z.infer<typeof segmentSourceSchema>;

// ============================================================
// @UC9092 — Response envelope + query
// ============================================================

export const growthResponseSchema = z
  .object({
    kpi: growthKpiSchema,
    funnel: funnelSchema,
    /** Chỉ segment có dữ liệu thật (referral/organic) — social absent (documented). */
    segments: z.array(segmentSourceSchema),
  })
  .strict();

export type GrowthResponse = z.infer<typeof growthResponseSchema>;

/** Query GET /admin/analytics/growth — kỳ {from,to} tuỳ chọn (ISO). Vắng ⇒ default (now, cửa sổ).
 * `format=csv` ⇒ export CSV (US-AE-IMP.F5-0116 · @UC9146); vắng ⇒ JSON. Mirror admin-reconcile. */
export const growthQuerySchema = z
  .object({
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    format: z.enum(["csv"]).optional(),
  })
  .strict();

export type GrowthQuery = z.infer<typeof growthQuerySchema>;
